import { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useToast } from "@/contexts/ToastContext"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { MultiSelect } from "@/components/ui/multi-select"
import { TagInput } from "@/components/ui/tag-input"
import { BRAZILIAN_STATES } from "@/data/brazilian-states"
import {
  handleCurrencyInput,
  handlePercentageInput,
  parseCurrencyToNumber,
  formatDecimalToBRL,
} from "@/utils/formatCurrency"
import { getSegmentos, getSegmentosSecundarios } from "@/services/companyService"
import {
  getCompanyProfile,
  updateCompanyProfile,
  getIBGECities,
  parsePgArray,
  uploadCompanyImage,
} from "../service/companyProfileService"
import type { CompanyProfileFormData } from "../types"
import type { SegmentoDataType, SegmentoSecundarioDataType } from "@/types/segmentos"
import { Icon } from "@/components/Atoms/Icon"

const SALES_CHANNELS = [
  { value: "1", label: "B2B" },
  { value: "2", label: "B2B2C" },
  { value: "3", label: "B2C" },
]

function normalizeStr(str: string): string {
  if (!str) return ""
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

function stripSitePrefix(url: string): string {
  if (!url) return ""
  return url.replace(/^https?:\/\//i, "").replace(/^www\./i, "")
}

function buildSiteUrl(value: string): string {
  if (!value) return ""
  const cleaned = stripSitePrefix(value)
  return `https://${cleaned}`
}

const FormFieldDisplay: React.FC<{
  label: string
  value?: string | null | number
  className?: string
}> = ({ label, value, className }) => (
  <div className={`flex flex-col space-y-1${className ? ` ${className}` : ""}`}>
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span className="text-base text-foreground">{value || "-"}</span>
  </div>
)

export default function CompanyProfilePage() {
  const { user, refreshUser } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [segments, setSegments] = useState<SegmentoDataType[]>([])
  const [secondarySegments, setSecondarySegments] = useState<SegmentoSecundarioDataType[]>([])
  const [cities, setCities] = useState<{ id: number; nome: string }[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [citySearch, setCitySearch] = useState("")
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false)
  const cityInputRef = useRef<HTMLInputElement>(null)
  const cityDropdownRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [empUid, setEmpUid] = useState((user as { empUid?: string } | null)?.empUid ?? "")

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !empUid) return

    setIsUploadingImage(true)
    try {
      const response = await uploadCompanyImage(empUid, file)
      const newLogoUrl = response.data.url

      const storedUser = localStorage.getItem("user")
      if (storedUser) {
        const userData = JSON.parse(storedUser)
        userData.emp_logo_url = newLogoUrl
        userData.empLogoUrl = newLogoUrl
        localStorage.setItem("user", JSON.stringify(userData))
      }
      refreshUser()
      toast.success("Logo da empresa atualizada!")
    } catch (error) {
      toast.danger("Erro ao fazer upload da imagem.")
    } finally {
      setIsUploadingImage(false)
    }
  }

  const form = useForm<CompanyProfileFormData>({
    defaultValues: {
      emp_fantasia: "",
      emp_faturamento_anual: "",
      emp_rentabilidade: "",
      emp_faturamento_2024: "",
      emp_margem_liquida_2024: "",
      emp_numero_funcionarios: "",
      emp_quantidade_socios: "",
      canal_venda: "",
      emp_estado: "",
      emp_cidade: "",
      emp_principal_catapp_id: "",
      emp_principal_subcatapp_id: "",
      emp_secundaria_catapp_id: "",
      emp_secundaria_subcatapp_id: "",
      emp_site: "",
      emp_instagram: "",
      emp_o_que_vende: "",
      emp_segmentos_estrategicos: [],
      emp_palavras_chave: [],
      emp_ciclo: "",
    },
  })

  const selectedState = form.watch("emp_estado")
  const selectedCity = form.watch("emp_cidade")

  const selectedPrincipalCat = form.watch("emp_principal_catapp_id")
  const selectedSecondaryCat = form.watch("emp_secundaria_catapp_id")

  const principalSubcategories = useMemo(() => {
    if (!selectedPrincipalCat) return []
    return secondarySegments.filter(
      (s) => s.subcatapp_catapp_id === parseInt(selectedPrincipalCat, 10)
    )
  }, [selectedPrincipalCat, secondarySegments])

  const secondarySubcategories = useMemo(() => {
    if (!selectedSecondaryCat) return []
    return secondarySegments.filter(
      (s) => s.subcatapp_catapp_id === parseInt(selectedSecondaryCat, 10)
    )
  }, [selectedSecondaryCat, secondarySegments])

  const filteredCities = useMemo(() => {
    if (!citySearch) return cities
    const normalized = normalizeStr(citySearch)
    return cities.filter((c) => normalizeStr(c.nome).includes(normalized))
  }, [cities, citySearch])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(e.target as Node) &&
        cityInputRef.current &&
        !cityInputRef.current.contains(e.target as Node)
      ) {
        setCityDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  useEffect(() => {
    Promise.all([getSegmentos(), getSegmentosSecundarios()])
      .then(([segRes, secSegRes]) => {
        setSegments(segRes.data)
        setSecondarySegments(secSegRes.data)
      })
      .catch(() => {
        toast.danger("Erro ao carregar categorias.")
      })
  }, [])

  const loadProfile = () => {
    setIsLoading(true)
    getCompanyProfile()
      .then(({ data }) => {
        if (data.uid) setEmpUid(data.uid)
        const siteValue = data.emp_site ? stripSitePrefix(data.emp_site) : ""
        form.reset({
          emp_fantasia: data.emp_fantasia || "",
          emp_faturamento_anual: formatDecimalToBRL(data.emp_faturamento_anual),
          emp_rentabilidade: formatDecimalToBRL(data.emp_rentabilidade),
          emp_faturamento_2024: formatDecimalToBRL(data.emp_faturamento_2024),
          emp_margem_liquida_2024: formatDecimalToBRL(data.emp_margem_liquida_2024),
          emp_numero_funcionarios: data.emp_numero_funcionarios?.toString() || "",
          emp_quantidade_socios: data.emp_quantidade_socios?.toString() || "",
          canal_venda: data.canal_venda != null ? String(data.canal_venda) : "",
          emp_estado: data.emp_estado || "",
          emp_cidade: data.emp_cidade || "",
          emp_principal_catapp_id: data.emp_principal_catapp_id?.toString() || "",
          emp_principal_subcatapp_id: data.emp_principal_subcatapp_id?.toString() || "",
          emp_secundaria_catapp_id: data.emp_secundaria_catapp_id?.toString() || "",
          emp_secundaria_subcatapp_id: data.emp_secundaria_subcatapp_id?.toString() || "",
          emp_site: siteValue,
          emp_instagram: data.emp_instagram || "",
          emp_o_que_vende: data.emp_o_que_vende || "",
          emp_segmentos_estrategicos: parsePgArray(data.emp_segmentos_estrategicos),
          emp_palavras_chave: parsePgArray(data.emp_palavras_chave),
          emp_ciclo: data.emp_ciclo || "",
        })
        setCitySearch(data.emp_cidade || "")
      })
      .catch(() => {
        toast.danger("Erro ao carregar dados da empresa.")
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(loadProfile, [form.reset])

  useEffect(() => {
    if (!selectedState) {
      setCities([])
      return
    }
    setLoadingCities(true)
    getIBGECities(selectedState)
      .then((data) => {
        setCities(data)
        const currentCity = form.getValues("emp_cidade")
        if (currentCity) {
          const normalizedCurrent = normalizeStr(currentCity)
          const match = data.find((c) => normalizeStr(c.nome) === normalizedCurrent)
          if (match && match.nome !== currentCity) {
            form.setValue("emp_cidade", match.nome)
            setCitySearch(match.nome)
          }
        }
      })
      .catch(() => {
        setCities([])
        toast.danger("Erro ao carregar cidades.")
      })
      .finally(() => setLoadingCities(false))
  }, [selectedState, form])

  const handleSelectCity = (cityName: string) => {
    form.setValue("emp_cidade", cityName)
    setCitySearch(cityName)
    setCityDropdownOpen(false)
  }

  const onSubmit = async (formData: CompanyProfileFormData) => {
    setIsSaving(true)
    try {
      await updateCompanyProfile({
        emp_fantasia: formData.emp_fantasia || null,
        emp_faturamento_anual: formData.emp_faturamento_anual
          ? parseCurrencyToNumber(formData.emp_faturamento_anual).toString()
          : null,
        emp_rentabilidade: formData.emp_rentabilidade
          ? parseCurrencyToNumber(formData.emp_rentabilidade).toString()
          : null,
        emp_faturamento_2024: formData.emp_faturamento_2024
          ? parseCurrencyToNumber(formData.emp_faturamento_2024).toString()
          : null,
        emp_margem_liquida_2024: formData.emp_margem_liquida_2024
          ? parseCurrencyToNumber(formData.emp_margem_liquida_2024).toString()
          : null,
        emp_numero_funcionarios: parseInt(formData.emp_numero_funcionarios) || 0,
        emp_quantidade_socios: parseInt(formData.emp_quantidade_socios) || 0,
        canal_venda: formData.canal_venda || null,
        emp_estado: formData.emp_estado || null,
        emp_cidade: formData.emp_cidade || null,
        emp_principal_catapp_id: formData.emp_principal_catapp_id
          ? parseInt(formData.emp_principal_catapp_id)
          : null,
        emp_principal_subcatapp_id: formData.emp_principal_subcatapp_id
          ? parseInt(formData.emp_principal_subcatapp_id)
          : null,
        emp_secundaria_catapp_id: formData.emp_secundaria_catapp_id
          ? parseInt(formData.emp_secundaria_catapp_id)
          : null,
        emp_secundaria_subcatapp_id: formData.emp_secundaria_subcatapp_id
          ? parseInt(formData.emp_secundaria_subcatapp_id)
          : null,
        emp_site: formData.emp_site ? buildSiteUrl(formData.emp_site) : null,
        emp_instagram: formData.emp_instagram || null,
        emp_o_que_vende: formData.emp_o_que_vende || null,
        emp_segmentos_estrategicos: formData.emp_segmentos_estrategicos.map(Number),
        emp_palavras_chave: formData.emp_palavras_chave,
        emp_ciclo: formData.emp_ciclo || null,
      })
      toast.success("Dados da empresa atualizados com sucesso!")
      setIsEditing(false)
    } catch {
      toast.danger("Erro ao salvar dados da empresa.")
    } finally {
      setIsSaving(false)
    }
  }

  if (!user) return <div>Carregando...</div>

  const values = form.getValues()
  const salesChannelDesc = SALES_CHANNELS.find((c) => c.value === values.canal_venda)?.label
  const stateDesc = BRAZILIAN_STATES.find((s) => s.sigla === values.emp_estado)?.nome
  const segmentDesc = segments.find(
    (s) => s.catapp_id === parseInt(values.emp_principal_catapp_id || "", 10)
  )?.catapp_descricao
  const principalSubSegmentDesc = secondarySegments.find(
    (s) => s.subcatapp_id === parseInt(values.emp_principal_subcatapp_id || "", 10)
  )?.subcatapp_descricao
  const secondarySegmentDesc = segments.find(
    (s) => s.catapp_id === parseInt(values.emp_secundaria_catapp_id || "", 10)
  )?.catapp_descricao
  const secondarySubSegmentDesc = secondarySegments.find(
    (s) => s.subcatapp_id === parseInt(values.emp_secundaria_subcatapp_id || "", 10)
  )?.subcatapp_descricao
  const strategicSegments = values.emp_segmentos_estrategicos
    .map((id) => segments.find((s) => s.catapp_id === Number(id))?.catapp_descricao)
    .filter((s): s is string => !!s)

  return (
    <main className="w-full animate-fade-in space-y-4 overflow-x-hidden p-4 pb-28 sm:space-y-6 sm:p-6 sm:pb-6">
      <div className="mx-auto space-y-4 sm:space-y-6 md:max-w-3xl">
        <div className="card-elevated flex items-center justify-between p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className="group relative flex size-12 shrink-0 items-center justify-center rounded-full bg-muted sm:size-20"
              onClick={() => fileInputRef.current?.click()}
            >
              {!isEditing && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {isUploadingImage ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/40">
                      <div className="size-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    </div>
                  ) : (
                    <div className="absolute -right-2 bottom-0 z-10 flex cursor-pointer items-center justify-center rounded-full transition-colors">
                      <div className="flex size-8 items-center justify-center rounded-full border bg-zinc-900 text-orange-500">
                        <Icon
                          name="camera"
                          className="bg-zinc-700 text-white opacity-0 transition-opacity group-hover:opacity-100"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {user.emp_logo_url ? (
                <img
                  src={user.emp_logo_url}
                  alt={user.emp_fantasia}
                  className="size-full shrink-0 rounded-full object-cover sm:size-20"
                />
              ) : (
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted sm:size-20">
                  <i className="fi fi-ts-building flex text-xl text-muted-foreground sm:text-2xl" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold text-foreground sm:text-2xl">
                Perfil da Empresa
              </h1>
              <p className="text-xs text-muted-foreground sm:text-sm">
                Gerencie as informações da sua empresa
              </p>
            </div>
          </div>
          {!isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              className="sm:flex"
            >
              <span className="hidden sm:flex">Editar Perfil</span>
              <Icon
                name="pen-circle"
                state="elegant"
                size={20}
              />
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="card-premium flex items-center justify-center p-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="text-sm text-muted-foreground">Carregando dados...</span>
            </div>
          </div>
        ) : isEditing ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 sm:space-y-6"
            >
              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-info flex" />
                  Informações Básicas
                </h3>
                <div className="card-premium space-y-4 p-3 sm:p-4">
                  <FormField
                    control={form.control}
                    name="emp_fantasia"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Fantasia</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Nome fantasia da empresa"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="emp_faturamento_2024"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Faturamento Anual (2024)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                R$
                              </span>
                              <Input
                                className="pl-10"
                                placeholder="0,00"
                                inputMode="numeric"
                                value={field.value}
                                onChange={(e) => {
                                  field.onChange(handleCurrencyInput(e.target.value))
                                }}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="emp_margem_liquida_2024"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Margem Líquida Anual (2024)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="pr-8"
                                placeholder="0,00"
                                inputMode="numeric"
                                value={field.value}
                                onChange={(e) => {
                                  field.onChange(handlePercentageInput(e.target.value))
                                }}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                %
                              </span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="emp_faturamento_anual"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Faturamento Anual (2025)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                R$
                              </span>
                              <Input
                                className="pl-10"
                                placeholder="0,00"
                                inputMode="numeric"
                                value={field.value}
                                onChange={(e) => {
                                  field.onChange(handleCurrencyInput(e.target.value))
                                }}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="emp_rentabilidade"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Margem Líquida Anual (2025)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="pr-8"
                                placeholder="0,00"
                                inputMode="numeric"
                                value={field.value}
                                onChange={(e) => {
                                  field.onChange(handlePercentageInput(e.target.value))
                                }}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                                %
                              </span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="emp_numero_funcionarios"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Colaboradores</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              placeholder="0"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="emp_quantidade_socios"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sócios</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              placeholder="0"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="canal_venda"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Canal de Venda</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o canal de venda" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SALES_CHANNELS.map((ch) => (
                              <SelectItem
                                key={ch.value}
                                value={ch.value}
                              >
                                {ch.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emp_ciclo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ciclo</FormLabel>
                        <FormControl>
                          <Input
                            className="pr-8"
                            placeholder="Qual o ciclo atual da sua empresa?"
                            inputMode="numeric"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-marker flex" />
                  Localização
                </h3>
                <div className="card-premium space-y-4 p-3 sm:p-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="emp_estado"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>UF</FormLabel>
                          <Select
                            onValueChange={(val) => {
                              field.onChange(val)
                              form.setValue("emp_cidade", "")
                              setCitySearch("")
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o estado" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {BRAZILIAN_STATES.map((state) => (
                                <SelectItem
                                  key={state.sigla}
                                  value={state.sigla}
                                >
                                  {state.sigla} - {state.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="emp_cidade"
                      render={() => (
                        <FormItem>
                          <FormLabel>Cidade</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <i className="fi fi-ts-search absolute left-3 top-1/2 flex -translate-y-1/2 text-xs text-muted-foreground" />
                              <Input
                                ref={cityInputRef}
                                className="pl-8"
                                placeholder={
                                  loadingCities
                                    ? "Carregando..."
                                    : !selectedState
                                      ? "Selecione o estado"
                                      : "Buscar cidade..."
                                }
                                disabled={!selectedState || loadingCities}
                                value={citySearch}
                                onChange={(e) => {
                                  setCitySearch(e.target.value)
                                  setCityDropdownOpen(true)
                                  if (!e.target.value) {
                                    form.setValue("emp_cidade", "")
                                  }
                                }}
                                onFocus={() => {
                                  if (selectedState && cities.length > 0) {
                                    setCityDropdownOpen(true)
                                  }
                                }}
                              />
                              {selectedCity && (
                                <button
                                  type="button"
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    form.setValue("emp_cidade", "")
                                    setCitySearch("")
                                    cityInputRef.current?.focus()
                                  }}
                                >
                                  <i className="fi fi-ts-cross-small flex text-xs" />
                                </button>
                              )}
                              {cityDropdownOpen && filteredCities.length > 0 && (
                                <div
                                  ref={cityDropdownRef}
                                  className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md"
                                >
                                  {filteredCities.map((city) => (
                                    <button
                                      key={city.id}
                                      type="button"
                                      className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
                                        selectedCity === city.nome
                                          ? "bg-accent font-medium text-accent-foreground"
                                          : ""
                                      }`}
                                      onClick={() => handleSelectCity(city.nome)}
                                    >
                                      {city.nome}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {cityDropdownOpen &&
                                citySearch &&
                                filteredCities.length === 0 &&
                                !loadingCities && (
                                  <div
                                    ref={cityDropdownRef}
                                    className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-3 text-center text-sm text-muted-foreground shadow-md"
                                  >
                                    Nenhuma cidade encontrada
                                  </div>
                                )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-apps flex" />
                  Categorias
                </h3>
                <div className="card-premium space-y-4 p-3 sm:p-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="emp_principal_catapp_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Categoria Principal</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value)
                              form.setValue("emp_principal_subcatapp_id", "")
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a categoria" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {segments
                                .filter((seg) => seg.catapp_id != null)
                                .map((seg) => (
                                  <SelectItem
                                    key={seg.catapp_id}
                                    value={String(seg.catapp_id)}
                                  >
                                    {seg.catapp_descricao}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="emp_principal_subcatapp_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subcategoria Principal</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={!selectedPrincipalCat || principalSubcategories.length === 0}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a subcategoria" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {principalSubcategories.map((seg) => (
                                <SelectItem
                                  key={seg.subcatapp_id}
                                  value={String(seg.subcatapp_id)}
                                >
                                  {seg.subcatapp_descricao}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="emp_secundaria_catapp_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Categoria Secundária</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value)
                              form.setValue("emp_secundaria_subcatapp_id", "")
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a categoria secundária" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {segments
                                .filter((seg) => seg.catapp_id != null)
                                .map((seg) => (
                                  <SelectItem
                                    key={seg.catapp_id}
                                    value={String(seg.catapp_id)}
                                  >
                                    {seg.catapp_descricao}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="emp_secundaria_subcatapp_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subcategoria Secundária</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={!selectedSecondaryCat || secondarySubcategories.length === 0}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a subcategoria secundária" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {secondarySubcategories.map((seg) => (
                                <SelectItem
                                  key={seg.subcatapp_id}
                                  value={String(seg.subcatapp_id)}
                                >
                                  {seg.subcatapp_descricao}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-globe flex" />
                  Presença Online
                </h3>
                <div className="card-premium space-y-4 p-3 sm:p-4">
                  <FormField
                    control={form.control}
                    name="emp_site"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Site da Empresa</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground sm:text-sm">
                              https://
                            </span>
                            <Input
                              className="pl-[4.5rem] sm:pl-[4.75rem]"
                              placeholder="www.suaempresa.com.br"
                              inputMode="url"
                              value={field.value}
                              onChange={(e) => {
                                const val = stripSitePrefix(e.target.value)
                                field.onChange(val)
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emp_instagram"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Instagram da Empresa</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                              @
                            </span>
                            <Input
                              className="pl-8"
                              placeholder="suaempresa"
                              value={field.value}
                              onChange={(e) => {
                                const val = e.target.value.replace(/^@/, "")
                                field.onChange(val)
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                  <i className="fi fi-ts-document flex" />
                  Sobre a Empresa
                </h3>
                <div className="card-premium space-y-4 p-3 sm:p-4">
                  <FormField
                    control={form.control}
                    name="emp_o_que_vende"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          O que sua empresa faz, que produtos ou serviços ela oferece?
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            rows={4}
                            placeholder="Descreva os produtos ou serviços que sua empresa oferece..."
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emp_segmentos_estrategicos"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Quais categorias de empresa são estratégicos para você se conectar?
                        </FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={segments
                              .filter((s) => s.catapp_id != null)
                              .map((s) => ({
                                value: String(s.catapp_id),
                                label: s.catapp_descricao,
                              }))}
                            selected={field.value}
                            onChange={field.onChange}
                            placeholder="Selecione as categorias estratégicos"
                            searchPlaceholder="Buscar categoria..."
                            emptyMessage="Nenhuma categoria encontrado."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emp_palavras_chave"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Palavras-chave relacionadas ao seu negócio</FormLabel>
                        <FormControl>
                          <TagInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Digite e pressione Enter ou ;"
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Separe por ponto e vírgula (;) ou Enter. Recomendado pelo menos 20
                          palavras-chave para otimizar seu alcance.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="bottom-0 left-0 z-40 flex w-full gap-2 bg-background/95 p-3 backdrop-blur-sm sm:relative sm:inset-auto sm:z-auto sm:w-auto sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false)
                    loadProfile()
                  }}
                  className="flex-1 sm:flex-initial"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 sm:flex-initial"
                >
                  {isSaving ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Salvando...
                    </>
                  ) : (
                    "Salvar Alterações"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            <div className="space-y-2 sm:space-y-3">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                <i className="fi fi-ts-info flex" />
                Informações Básicas
              </h3>
              <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
                <FormFieldDisplay
                  label="Nome Fantasia"
                  value={values.emp_fantasia}
                  className="col-span-full"
                />
                <FormFieldDisplay
                  label="Faturamento Anual (2024)"
                  value={values.emp_faturamento_2024}
                />
                <FormFieldDisplay
                  label="Margem Líquida Anual (2024)"
                  value={
                    values.emp_margem_liquida_2024 ? `${values.emp_margem_liquida_2024}%` : "-"
                  }
                />
                <FormFieldDisplay
                  label="Faturamento Anual (2025)"
                  value={values.emp_faturamento_anual}
                />
                <FormFieldDisplay
                  label="Margem Líquida Anual (2025)"
                  value={values.emp_rentabilidade ? `${values.emp_rentabilidade}%` : "-"}
                />
                <FormFieldDisplay
                  label="Colaboradores"
                  value={values.emp_numero_funcionarios}
                />
                <FormFieldDisplay
                  label="Sócios"
                  value={values.emp_quantidade_socios}
                />
                <FormFieldDisplay
                  label="Canal de Venda"
                  value={salesChannelDesc}
                />
                <FormFieldDisplay
                  label="Ciclo"
                  value={values.emp_ciclo}
                />
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                <i className="fi fi-ts-marker flex" />
                Localização
              </h3>
              <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
                <FormFieldDisplay
                  label="Estado"
                  value={stateDesc}
                />
                <FormFieldDisplay
                  label="Cidade"
                  value={values.emp_cidade}
                />
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                <i className="fi fi-ts-apps flex" />
                Categorias
              </h3>
              <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
                <FormFieldDisplay
                  label="Categoria Principal"
                  value={segmentDesc}
                />
                <FormFieldDisplay
                  label="Subcategoria Principal"
                  value={principalSubSegmentDesc}
                />
                <FormFieldDisplay
                  label="Categoria Secundária"
                  value={secondarySegmentDesc}
                />
                <FormFieldDisplay
                  label="Subcategoria Secundária"
                  value={secondarySubSegmentDesc}
                />
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                <i className="fi fi-ts-globe flex" />
                Presença Online
              </h3>
              <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
                <FormFieldDisplay
                  label="Site da Empresa"
                  value={values.emp_site ? buildSiteUrl(values.emp_site) : "-"}
                />
                <FormFieldDisplay
                  label="Instagram"
                  value={values.emp_instagram ? `@${values.emp_instagram}` : "-"}
                />
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:text-sm">
                <i className="fi fi-ts-document flex" />
                Sobre a Empresa
              </h3>
              <div className="card-premium grid grid-cols-1 gap-4 p-3 sm:p-4">
                <FormFieldDisplay
                  label="O que sua empresa faz?"
                  value={values.emp_o_que_vende}
                />
                <div className="flex flex-col space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    Categorias Estratégicas
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {strategicSegments.length > 0 ? (
                      strategicSegments.map((segment) => (
                        <Badge
                          key={segment}
                          variant="secondary"
                          className="font-normal"
                        >
                          {segment}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-base text-foreground">-</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">Palavras-chave</span>
                  <div className="flex flex-wrap gap-2">
                    {values.emp_palavras_chave.length > 0 ? (
                      values.emp_palavras_chave.map((keyword) => (
                        <Badge
                          key={keyword}
                          variant="secondary"
                          className="font-normal"
                        >
                          {keyword}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-base text-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}
